/**
 * Cookie-consent state, split out of CookieBanner so other components can read
 * it without importing a component module.
 *
 * The support widget is the reason: it sits in the opposite bottom corner, and
 * the banner is full-width along the bottom of the viewport, so on a first visit
 * the banner would cover the chat button completely.
 */

export interface CookieConsent {
  necessary: boolean
  analytics: boolean
  marketing: boolean
  timestamp: string
}

const STORAGE_KEY = 'evtimia-cookie-consent'

export function getStoredConsent(): CookieConsent | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    return stored ? (JSON.parse(stored) as CookieConsent) : null
  } catch {
    return null
  }
}

export function storeConsent(consent: CookieConsent) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(consent))
  } catch {
    // Private modes and sandboxed frames throw. The banner still closes; the
    // visitor is simply asked again next time, which is the safe direction.
  }
}

/** Whether the visitor has already answered the consent question. */
export function hasCookieDecision(): boolean {
  return getStoredConsent() !== null
}

/** Fired with `detail.open` whenever the banner opens or closes. */
export const COOKIE_BANNER_EVENT = 'evtimia:cookie-banner'

export function announceCookieBanner(open: boolean) {
  window.dispatchEvent(new CustomEvent(COOKIE_BANNER_EVENT, { detail: { open } }))
}
